import { Building2, Loader2, QrCode } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import { formatCurrency } from '@meiuca/web-foundation'
import { modalitiesManager } from '../../../managers/modalitiesManager'
import type { SelectedCourt, SelectedSlotDetail, SummaryCheckoutPricing } from '../types'
import type {
  CheckoutPaymentMethod,
  CheckoutPaymentMethodId
} from '@/services/venueBookingService'

interface SummaryStepProps {
  selectedModality: string | null
  selectedSlotDetails: SelectedSlotDetail[]
  /** Valores do checkout-options (de→por por item + totais); null até carregar. */
  checkoutPricing?: SummaryCheckoutPricing | null
  // Payment selection (provided by BookingPage; absent in read-only usages)
  paymentMethods?: CheckoutPaymentMethod[]
  loadingPayments?: boolean
  paymentsError?: boolean
  onRetryPayments?: () => void
  selectedMethodId?: CheckoutPaymentMethodId | null
  onSelectMethod?: (id: CheckoutPaymentMethodId) => void
}

/** Início do slot em epoch ms — a MESMA construção do builder de items da página. */
const slotStartMillis = (slot: SelectedSlotDetail) => {
  const [hours, minutes] = slot.time.split(':').map(Number)
  const startDate = new Date(slot.date + 'T00:00:00')
  startDate.setHours(hours, minutes, 0, 0)
  return startDate.getTime()
}

const METHOD_SUBTITLE: Record<CheckoutPaymentMethodId, string> = {
  pix_online: 'Pagamento instantâneo',
  manual: 'Combine direto com a arena'
}

export function SummaryStep({
  selectedModality,
  selectedSlotDetails,
  checkoutPricing,
  paymentMethods,
  loadingPayments,
  paymentsError,
  onRetryPayments,
  selectedMethodId,
  onSelectMethod
}: SummaryStepProps) {
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-')
    return `${day}/${month}/${year}`
  }

  // "Por" de um item: resposta do checkout (autoritativa) → valor da listagem
  // → tabela. A criação continua enviando SEMPRE o preço de tabela.
  const effectivePrice = (slot: SelectedSlotDetail, court: SelectedCourt) => {
    const fromResponse = checkoutPricing?.finalPriceByItemKey[`${court.id}|${slotStartMillis(slot)}`]
    return fromResponse ?? court.campaignPricePerHour ?? court.pricePerHour ?? 0
  }

  // Order by start instant — selection can add a slot before the block (13h
  // tapped after 14h/15h), which should still read chronologically here.
  const sortedSlots = [...selectedSlotDetails].sort((a, b) => slotStartMillis(a) - slotStartMillis(b))

  const dateFormatted = sortedSlots.length > 0 ? formatDate(sortedSlots[0].date) : ''
  const tableTotal = selectedSlotDetails.reduce(
    (total, slot) => total + slot.courts.reduce((sum, court) => sum + (court.pricePerHour ?? 0), 0),
    0
  )
  const effectiveTotal = selectedSlotDetails.reduce(
    (total, slot) => total + slot.courts.reduce((sum, court) => sum + effectivePrice(slot, court), 0),
    0
  )
  const subtotal = checkoutPricing?.totalAmountCents ?? tableTotal
  const discount = checkoutPricing?.discountAmountCents ?? tableTotal - effectiveTotal
  const totalPrice = checkoutPricing?.finalAmountCents ?? effectiveTotal

  const showPayment = onSelectMethod !== undefined

  return (
    <div className="flex flex-col h-full px-6 pt-4 pb-6">
      <div className="space-y-4">
        {/* Modality */}
        <div className="p-3 bg-background-secondary rounded-lg">
          <div className="text-text-secondary text-xs mb-0.5">Modalidade</div>
          <div className="font-medium text-sm">{selectedModality ? modalitiesManager.getModalityDescription(selectedModality) : 'Não selecionada'}</div>
        </div>

        {/* Reservations */}
        {selectedSlotDetails.length === 0 ? (
          <div className="text-text-secondary italic text-center py-4 text-sm">
            Nenhum horário selecionado
          </div>
        ) : (
          <div className="border border-border-light rounded-lg overflow-hidden">
            {/* Date Header */}
            <div className="bg-background-secondary px-3 py-2 border-b border-border-light">
              <div className="font-semibold text-sm text-text-primary">{dateFormatted}</div>
            </div>

            {/* Time Slots */}
            <div className="divide-y divide-border-light">
              {sortedSlots.map((slot, slotIndex) => (
                <div key={slotIndex} className="p-3">
                  <div className="font-medium text-sm text-text-primary mb-2">{slot.time}</div>
                  <div className="space-y-1.5 pl-3">
                    {slot.courts.map((court) => {
                      const table = court.pricePerHour ?? 0
                      const effective = effectivePrice(slot, court)
                      return (
                        <div key={court.id} className="flex items-center justify-between text-xs">
                          <span className="text-text-secondary">{court.name}</span>
                          {effective < table ? (
                            // Slot descontado lê "de → por" (sem nome de campanha).
                            <span>
                              <span className="text-text-tertiary line-through mr-1.5">
                                {formatCurrency(table)}
                              </span>
                              <span className="text-text-primary font-medium">
                                {formatCurrency(effective)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-text-primary font-medium">
                              {formatCurrency(table)}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total Geral (com subtotal + linha genérica de desconto quando há campanha) */}
        {selectedSlotDetails.length > 0 && (
          <div className="p-3 bg-brand-primary/5 rounded-lg border border-brand-primary/20 space-y-1.5">
            {discount > 0 && (
              <>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-text-secondary">Subtotal</span>
                  <span className="text-text-secondary">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-text-secondary">Desconto</span>
                  <span className="text-feedback-success font-medium">
                    −{formatCurrency(discount)}
                  </span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center">
              <div className="font-semibold text-base">Total</div>
              <div className="text-lg font-bold text-brand-primary">{formatCurrency(totalPrice)}</div>
            </div>
          </div>
        )}

        {/* Payment method */}
        {showPayment && (
          <div className="space-y-3">
            <div className="text-text-secondary text-xs font-semibold uppercase tracking-wide">
              Forma de pagamento
            </div>

            {loadingPayments ? (
              <div className="flex items-center gap-2 text-text-secondary text-sm py-3">
                <Loader2 className="w-4 h-4 animate-spin" />
                Carregando formas de pagamento...
              </div>
            ) : paymentsError ? (
              <div className="text-center py-3 space-y-1">
                <Caption className="text-feedback-error">
                  Não foi possível carregar as formas de pagamento.
                </Caption>
                {onRetryPayments && (
                  <button
                    type="button"
                    onClick={onRetryPayments}
                    className="text-brand-primary text-sm font-medium"
                  >
                    Tentar novamente
                  </button>
                )}
              </div>
            ) : (paymentMethods?.length ?? 0) === 0 ? (
              <Caption className="text-text-secondary">
                Nenhuma forma de pagamento disponível para esta reserva.
              </Caption>
            ) : (
              <div className="space-y-2">
                {paymentMethods?.map((method) => {
                  const isSelected = method.id === selectedMethodId
                  const Icon = method.id === 'pix_online' ? QrCode : Building2
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => onSelectMethod?.(method.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-brand-primary bg-brand-primary/5'
                          : 'border-border-light hover:bg-background-secondary'
                      }`}
                    >
                      <Icon className="w-5 h-5 text-brand-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <Body className="text-sm font-medium text-text-primary m-0">
                          {method.title}
                        </Body>
                        <Caption className="text-text-secondary">
                          {METHOD_SUBTITLE[method.id]}
                        </Caption>
                      </div>
                      <span
                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          isSelected ? 'border-brand-primary bg-brand-primary' : 'border-border-light'
                        }`}
                      />
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
